import { Box, Flex, HStack, Text } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { getBrand } from '../brand/index.js'

/**
 * Navy app footer for Learn (replaces the old plain centered footer).
 * Brand wordmark + tagline on a dark band, matching the blog's rich footer.
 * No marketing nav links here — those live on the public blog; the app footer
 * stays minimal and free of dead links. The one exception is the brand's legal
 * pages (terms, privacy, refunds): the checkout domain must link them.
 */
const LEGAL_DOCS = [
  { doc: 'terms', label: 'legalTerms' },
  { doc: 'privacy', label: 'legalPrivacy' },
  { doc: 'refunds', label: 'legalRefunds' },
] as const

export function LearnFooter({ lang }: { lang: string }) {
  const brand = getBrand()
  const { t } = useTranslation()
  const tagline = brand.learn.footer[lang] || t('footer', { brand: brand.name })

  return (
    // Dark navy band (matches the blog footer) — intentionally outside the
    // light-mode ACP token palette; text colors are translucent white so they
    // ride on the fixed navy without needing dedicated dark-mode tokens.
    <Box as="footer" bg="#0E1320" color="rgba(255,255,255,0.66)" pt="3rem" pb="2.4rem" mt="4.5rem">
      <Flex
        maxW="960px"
        mx="auto"
        px={4}
        align="center"
        justify="space-between"
        gap={4}
        flexWrap="wrap"
      >
        {/* Real logo wordmark (inverted to white), matching the blog footer.
            A brand with no inverted variant falls back to its regular one. */}
        <img
          src={brand.assets.logoLight || brand.assets.logo}
          alt={brand.name}
          width={200}
          height={28}
          style={{ height: '1.3rem', width: 'auto', display: 'block', opacity: 0.92 }}
        />
        <Text fontFamily="mono" fontSize="0.72rem" color="rgba(255,255,255,0.42)">
          {tagline}
        </Text>
        {brand.legal && (
          <HStack as="nav" gap={4} fontSize="0.78rem">
            {LEGAL_DOCS.map(({ doc, label }) => (
              <a
                key={doc}
                href={brand.legal![doc][lang] || brand.legal![doc].en}
                style={{ color: 'rgba(255,255,255,0.66)', textDecoration: 'none' }}
              >
                {t(label)}
              </a>
            ))}
          </HStack>
        )}
      </Flex>
    </Box>
  )
}
